import { useEffect, useRef } from "react";

type OverlayEntry = {
  dismiss: () => void;
  disableClose: () => boolean;
};

const overlayStack: OverlayEntry[] = [];

let escapeKeyActive = false;

/** True when an open Material Web menu (Autocomplete, Menu, MultiSelect, etc.) is present. */
export function hasTransientPopupOpen() {
  return Boolean(document.querySelector("md-menu[open]"));
}

function dismissTopOverlay() {
  const top = overlayStack.at(-1);
  if (!top || top.disableClose()) return;
  top.dismiss();
}

export function isOverlayEscapeKeyActive() {
  return escapeKeyActive;
}

/** True when a Dialog / SideSheet / BottomDrawer is registered on the dismiss stack. */
export function hasOpenOverlay() {
  return overlayStack.length > 0;
}

function handleDocumentKeyDownCapture(event: KeyboardEvent) {
  if (event.key !== "Escape" || event.repeat) return;
  if (overlayStack.length === 0) return;
  escapeKeyActive = true;

  // Snapshot before deferring: Autocomplete and similar close md-menu on Escape
  // during target/bubble, so a post-microtask query would miss the popup that
  // owned this keypress.
  const hadTransientPopup = hasTransientPopupOpen();

  // Capture runs before target/bubble (including window listeners). Defer so
  // preventDefault() from nested typeaheads and Algolia can still win, and so
  // stopPropagation cannot leave this flag stuck.
  queueMicrotask(() => {
    const shouldDismiss = escapeKeyActive && !event.defaultPrevented && !hadTransientPopup;
    escapeKeyActive = false;
    if (!shouldDismiss) return;
    dismissTopOverlay();
  });
}

function attachEscapeListener() {
  if (overlayStack.length !== 1) return;
  document.addEventListener("keydown", handleDocumentKeyDownCapture, true);
}

function detachEscapeListener() {
  if (overlayStack.length !== 0) return;
  document.removeEventListener("keydown", handleDocumentKeyDownCapture, true);
  escapeKeyActive = false;
}

export function resetOverlayDismissStackForTests() {
  overlayStack.length = 0;
  escapeKeyActive = false;
  document.removeEventListener("keydown", handleDocumentKeyDownCapture, true);
}

export function useOverlayDismiss({
  open,
  disableClose = false,
  dismiss
}: {
  open: boolean;
  disableClose?: boolean;
  dismiss: () => void;
}) {
  const dismissRef = useRef(dismiss);
  dismissRef.current = dismiss;
  const disableCloseRef = useRef(disableClose);
  disableCloseRef.current = disableClose;

  useEffect(() => {
    if (!open) return;

    const entry: OverlayEntry = {
      dismiss: () => dismissRef.current(),
      disableClose: () => disableCloseRef.current
    };
    overlayStack.push(entry);
    attachEscapeListener();

    return () => {
      const index = overlayStack.lastIndexOf(entry);
      if (index >= 0) overlayStack.splice(index, 1);
      detachEscapeListener();
    };
  }, [open]);
}
